import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
} from "react";
import {
    Box,
    Typography,
    InputAdornment,
    TextField,
    ThemeProvider,
    createTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OuterDiv from "components/Common/OuterDivComponent";
import { mergeIntegrationService } from "services/mergeIntegration";
import { RootState } from "store";
import { useSelector } from "react-redux";
import CampaignTableSkeleton from "../PreLoadSkeletons/CampaignTableSkeleton";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import AtsLeadDetailsDrawer from "./AtsLeadDetailsDrawer";
import AtsLeadsTable from "./AtsLeadsTable";
import { debounce } from "lodash";
import { AtsSortConfig } from "types/MergeIntegration";

// ATS API Response Interfaces
export interface AtsCandidate {
    _id: string;
    id: string;
    username?: string; // Added for LeadsTable compatibility (maps to id)
    email_addresses: Array<{
        value: string;
        email_address_type: string | null;
    }>;
    first_name: string;
    last_name: string;
    title: string;
    ih_job_id: string;
    ih_candidate_id: string;
    ih_track_id: string;
    added_into_job?: boolean;
    evaluation_details: {
        candidate_score: number;
        match_strengths: string;
        match_gaps: string;
        recommendation: string;
        growth_potential: string;
        risk_factors: string;
        cultural_fit: string;
        interview_focus: string;
    };
}


const theme = createTheme({
    palette: {
        primary: {
            main: "#5175e0",
        },
        background: {
            default: "#f5f5f5",
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    minWidth: "auto",
                    padding: "12px 16px",
                    "&.Mui-selected": {
                        color: "#5175e0",
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: "#5175e0",
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                },
                head: {
                    backgroundColor: "#f8f9fa",
                    fontWeight: 600,
                    color: "#5f6368",
                },
            },
        },
    },
});



const AtsCandidatesDetails = () => {
    const expertId = useSelector((state: RootState) => state.auth.user.expertId);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const jId = params.get("jobId") || "";
    const [jobId] = useState(jId);

    const [allLeads, setAllLeads] = useState<AtsCandidate[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [firstLoad, setFirstLoad] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [openLeadDrawer, setOpenLeadDrawer] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState<AtsCandidate | null>(null);
    const [totalProfiles, setTotalProfiles] = useState<number>(0);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [sortConfig, setSortConfig] = useState<AtsSortConfig>({
        sortBy: null,
        sortOrder: 'desc'
    });

    const leadsPerPage = 10;

    const debouncedSearch = useCallback(
        debounce((searchValue: string) => {
            setDebouncedSearchText(searchValue);
            setCurrentPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    };

    const handleSortChange = (columnId: string) => {
        setSortConfig(prev => {
            if (prev.sortBy === columnId) {
                return {
                    sortBy: columnId as AtsSortConfig['sortBy'],
                    sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc'
                };
            }
            return {
                sortBy: columnId as AtsSortConfig['sortBy'],
                sortOrder: 'desc'
            };
        });
        setCurrentPage(1);
    };

    const fetchAllCampaignLeads = useCallback(async () => {
        try {
            if (firstLoad) {
                setLoading(true);
            } else {
                setTableLoading(true);
            }

            const payload = {
                expertId: expertId,
                jobId: jobId,
                limit: leadsPerPage,
                offset: (currentPage - 1) * leadsPerPage,
                ...(debouncedSearchText && { textToSearch: debouncedSearchText }),
                ...(sortConfig.sortBy && {
                    sortBy: sortConfig.sortBy,
                    sortOrder: sortConfig.sortOrder
                })
            };

            const response = await mergeIntegrationService.getAtsCandidates(payload);
            const atsCandidates: AtsCandidate[] = response?.output?.candidates || [];

            // Add username field for LeadsTable compatibility
            const candidatesWithUsername = atsCandidates.map(candidate => ({
                ...candidate,
                username: candidate.id
            }));

            setAllLeads(candidatesWithUsername);
            setTotalProfiles(response?.output?.total_count || 0);
        } catch (error) {
            console.log("Error fetching ATS candidates", error);
            toast.error("Failed to fetch ATS candidates");
        } finally {
            if (firstLoad) {
                setLoading(false);
                setFirstLoad(false);
            } else {
                setTableLoading(false);
            }
        }
    }, [jobId, expertId, currentPage, firstLoad, debouncedSearchText, sortConfig]);

    useEffect(() => {
        if (expertId && jobId) {
            fetchAllCampaignLeads();
        }
    }, [expertId, jobId, currentPage, debouncedSearchText, sortConfig]);

    if (loading) {
        return <CampaignTableSkeleton />;
    }

    return (
        <OuterDiv style={{ background: "#F8FAFF" }}>
            <ThemeProvider theme={theme}>
                <Box className="d-flex justify-content-between align-items-center bg-white px-4 py-3 border-bottom w-100">
                    <Box>
                        <Typography fontSize="16px">ATS Candidates</Typography>
                    </Box>
                </Box>
                <Box className="d-flex h-100 w-100 p-3">
                    <Box className="flex-grow-1 overflow-auto">
                        <Box
                            className="p-3"
                            sx={{
                                bgcolor: "#fff",
                                border: "1px solid #DCDFE5",
                                borderRadius: "8px",
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 2,
                                        justifyContent: "space-between",
                                        mb: 2,
                                    }}
                                >
                                    <Box>
                                        <TextField
                                            placeholder="Search with keywords..."
                                            value={searchText}
                                            onChange={handleSearchChange}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            variant="outlined"
                                            size="small"
                                            sx={{ width: 250 }}
                                        />
                                    </Box>
                                </Box>

                                {tableLoading ? (
                                    <CampaignTableSkeleton />
                                ) : (
                                    <AtsLeadsTable
                                        leads={allLeads}
                                        totalProfiles={totalProfiles}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        sortConfig={sortConfig}
                                        onSortChange={handleSortChange}
                                        onLeadClick={(candidate) => {
                                            setSelectedCandidate(candidate);
                                            setOpenLeadDrawer(true);
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>

            {selectedCandidate && (
                <AtsLeadDetailsDrawer
                    open={openLeadDrawer}
                    onClose={() => setOpenLeadDrawer(false)}
                    candidate={selectedCandidate}
                />
            )}
        </OuterDiv>
    );
};

export default React.memo(AtsCandidatesDetails);